import './Team.css';

import { getAuth } from 'firebase/auth';
import { useEffect, useState } from 'react';

import { apiService } from '../../../apiService';
import { useUserInfo } from '../../../hooks/useUserInfo';
import { parseISOWithoutConversion, sortEmployeesByName } from '../../../utils';
import MultiOrgEmployeeList from './MultiOrgEmployeeList';
import SingleOrgEmployeeList from './SingleOrgEmployeeList';

const Team = () => {
  const {
    isMultiOrg,
    isLoading: userInfoLoading,
    error: userInfoError,
  } = useUserInfo();
  const [searchTerm, setSearchTerm] = useState('');
  const [flagSearchTerm, setFlagSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [employeeData, setEmployeeData] = useState({});
  const [locationData, setLocationData] = useState({});
  const [adminData, setAdminData] = useState([]);
  const [flagHistoryData, setFlagHistoryData] = useState([]);
  const [filteredFlagHistory, setFilteredFlagHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userInfoLoading || userInfoError) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (!currentUser) {
          throw new Error('User not authenticated');
        }

        const uid = currentUser.uid;
        let employeesUrl = `/api/get-manager-employees?uid=${uid}`;

        const employees = await apiService.get(employeesUrl);

        if (isMultiOrg) {
          const { locations, admins } = organizeDataForAdmin(employees);
          setLocationData(locations);
          setAdminData(admins);
        } else {
          setEmployeeData(organizeEmployeesByFirstName(employees));
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [isMultiOrg, userInfoLoading, userInfoError]);

  // Fetch flagged employees - re-fetches when selectedDate changes
  useEffect(() => {
    const fetchFlaggedEmployees = async () => {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (!currentUser || userInfoLoading) {
          return;
        }

        const uid = currentUser.uid;
        let flaggedUrl = `/api/get-manager-flagged-employees?uid=${uid}`;

        // Add date filter only when a date is selected
        if (selectedDate) {
          flaggedUrl += `&start_date=${selectedDate}&end_date=${selectedDate}`;
        }

        const flaggedResponseData = await apiService.get(flaggedUrl);
        const flaggedEmployees = flaggedResponseData.body || [];

        const organizedFlags = organizeFlagsByDate(flaggedEmployees);
        setFlagHistoryData(organizedFlags);
        setFilteredFlagHistory(organizedFlags);
      } catch (err) {
        console.error('Error fetching flagged employees:', err);
      }
    };

    fetchFlaggedEmployees();
  }, [selectedDate, isMultiOrg, userInfoLoading]);

  useEffect(() => {
    filterFlagHistory();
  }, [flagSearchTerm, flagHistoryData]);

  const organizeDataForAdmin = (employees) => {
    const locations = {};
    const admins = [];

    employees?.forEach((employee, index) => {
      const companyName = employee.company_name || 'Unknown Organization';

      if (employee.user_type === 'admin') {
        admins.push({
          id: index,
          firstName: employee.first_name || '',
          lastName: employee.last_name || '',
          fullName: `${employee.first_name || ''} ${employee.last_name || ''}`,
          companyName: companyName,
        });
      } else if (employee.user_type === 'location_placeholder') {
        if (!locations[companyName]) {
          locations[companyName] = {
            employees: [],
            managers: [],
          };
        }
      } else {
        if (!locations[companyName]) {
          locations[companyName] = {
            employees: [],
            managers: [],
          };
        }

        if (employee.first_name && employee.last_name) {
          const person = {
            id: index,
            firstName: employee.first_name,
            lastName: employee.last_name,
            fullName: `${employee.first_name} ${employee.last_name}`,
            userType: employee.user_type || 'employee',
          };

          if (employee.user_type === 'manager') {
            locations[companyName].managers.push(person);
          } else {
            locations[companyName].employees.push(person);
          }
        }
      }
    });

    const camelCaseKeys = { firstNameKey: 'firstName', lastNameKey: 'lastName' };

    // Sort employees and managers within each location by first name, then last name
    Object.keys(locations).forEach((location) => {
      locations[location].employees = sortEmployeesByName(
        locations[location].employees,
        camelCaseKeys,
      );
      locations[location].managers = sortEmployeesByName(
        locations[location].managers,
        camelCaseKeys,
      );
    });

    // Sort admins by first name, then last name
    const sortedAdmins = sortEmployeesByName(admins, camelCaseKeys);

    // Sort locations alphabetically by name
    const sortedLocations = Object.keys(locations)
      .sort((a, b) => a.localeCompare(b))
      .reduce((acc, key) => {
        acc[key] = locations[key];
        return acc;
      }, {});

    return { locations: sortedLocations, admins: sortedAdmins };
  };

  const organizeEmployeesByFirstName = (employees) => {
    const organized = {};

    employees?.forEach((employee, index) => {
      if (
        employee.user_type === 'admin' ||
        employee.user_type === 'location_placeholder' ||
        !employee.first_name ||
        !employee.last_name
      ) {
        return;
      }

      const firstLetter = employee.first_name.charAt(0).toUpperCase();

      if (!organized[firstLetter]) {
        organized[firstLetter] = [];
      }

      organized[firstLetter].push({
        id: index,
        firstName: employee.first_name,
        lastName: employee.last_name,
        fullName: `${employee.first_name} ${employee.last_name}`,
      });
    });

    const camelCaseKeys = { firstNameKey: 'firstName', lastNameKey: 'lastName' };

    // Sort employees within each letter group by first name, then last name
    Object.keys(organized).forEach((letter) => {
      organized[letter] = sortEmployeesByName(organized[letter], camelCaseKeys);
    });

    return Object.keys(organized)
      .sort()
      .reduce((acc, key) => {
        acc[key] = organized[key];
        return acc;
      }, {});
  };

  const formatDateFromParsed = (parsed) => {
    if (!parsed) return '';
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const dayName = days[parsed.dayOfWeek];
    return `${dayName}, ${parsed.month}/${parsed.day}/${parsed.year}`;
  };

  const formatTimeFromParsed = (parsed) => {
    if (!parsed) return '';
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = days[parsed.dayOfWeek];

    let hours = parsed.hours;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    const minutes = parsed.minutes.toString().padStart(2, '0');

    return `${dayName} ${parsed.month}/${parsed.day} ${hours}:${minutes} ${ampm}`;
  };

  const organizeFlagsByDate = (flaggedEmployees) => {
    const groupedByDate = {};

    flaggedEmployees.forEach((employee, index) => {
      const isoString = employee.created_dt_in_org_zone;
      const parsed = parseISOWithoutConversion(isoString);
      const formattedDate = formatDateFromParsed(parsed);

      if (!groupedByDate[formattedDate]) {
        groupedByDate[formattedDate] = [];
      }

      const firstName = employee.first_name || '';
      const lastName = employee.last_name || '';

      groupedByDate[formattedDate].push({
        id: index,
        name: `${firstName} ${lastName}`,
        firstName,
        lastName,
        time: formatTimeFromParsed(parsed) + ` ` + employee.timezone,
        originalDate: new Date(isoString),
        companyName: employee.company_name || 'Unknown Organization',
      });
    });

    const camelCaseKeys = { firstNameKey: 'firstName', lastNameKey: 'lastName' };

    return Object.entries(groupedByDate)
      .map(([date, flags]) => ({
        date,
        originalDate: flags[0].originalDate,
        // Sort flags alphabetically by first name, then last name
        flags: sortEmployeesByName(flags, camelCaseKeys),
      }))
      .sort((a, b) => {
        return b.originalDate - a.originalDate;
      });
  };

  const filterFlagHistory = () => {
    let filtered = [...flagHistoryData];

    // Only filter by search term - date filtering is handled by the API
    if (flagSearchTerm) {
      filtered = filtered
        .map((dateGroup) => {
          const filteredFlags = dateGroup.flags.filter((flag) =>
            flag.name.toLowerCase().includes(flagSearchTerm.toLowerCase()),
          );

          return {
            ...dateGroup,
            flags: filteredFlags,
          };
        })
        .filter((dateGroup) => dateGroup.flags.length > 0);
    }

    setFilteredFlagHistory(filtered);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFlagSearchChange = (event) => {
    setFlagSearchTerm(event.target.value);
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  if (userInfoError) {
    return <div className='error'>Failed to determine user permissions</div>;
  }

  if (loading || userInfoLoading) {
    return <div className='loading'>Loading...</div>;
  }

  if (error) {
    return <div className='error'>Error: {error}</div>;
  }

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className='team-page'>
      <div className='team-header'>
        <h1>{isMultiOrg ? 'All Team Members' : 'My Employees'}</h1>
      </div>

      <div className='team-content'>
        <div className='employee-list-section'>
          {isMultiOrg ? (
            <MultiOrgEmployeeList
              locationData={locationData}
              adminData={adminData}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
            />
          ) : (
            <SingleOrgEmployeeList
              employeeData={employeeData}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
            />
          )}
        </div>

        <div className='vertical-divider'></div>

        <div className='flag-history-section'>
          <div className='section-header'>
            <h2>Employee Impairment History</h2>
          </div>

          <div className='flag-filters'>
            <input
              type='text'
              placeholder='Search for identified impairment'
              value={flagSearchTerm}
              onChange={handleFlagSearchChange}
              className='flag-search-input'
            />
            <input
              type='date'
              value={selectedDate}
              onChange={handleDateChange}
              max={today}
              className='flag-date-input'
            />
          </div>

          <div className='flag-history-container'>
            {filteredFlagHistory.length > 0 ? (
              filteredFlagHistory.map((dateGroup, index) => (
                <div key={index} className='flag-date-group'>
                  <div className='flag-date'>{dateGroup.date}</div>

                  {dateGroup.flags.map((flag, flagIndex) => (
                    <div key={flagIndex} className='flag-item'>
                      <div className='flag-header'>
                        <div className='flag-name'>{flag.name}</div>
                        {isMultiOrg && flag.companyName && (
                          <div className='flag-company'>{flag.companyName}</div>
                        )}
                      </div>
                      <div className='flag-time'>{flag.time}</div>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className='no-flags'>
                {selectedDate
                  ? 'No impaired employees found for the selected date'
                  : flagSearchTerm
                    ? 'No impaired employees found matching your search'
                    : 'No impaired employees yet'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Team;
